/**
 * ¿La persona llegó al final de un texto largo? (piloto 2026-09-30, aviso de privacidad).
 *
 * `Screen` no tiene un contenedor con scroll propio: se desplaza la **ventana** (`min-h-dvh` y el
 * pie `sticky bottom-0`). Por eso el centinela se observa contra el viewport (`root: null`) con un
 * margen inferior negativo del alto del pie fijo: un centinela tapado por el pie aún no se "vio".
 *
 * Además, llegar al borde inferior del documento (o que no haya nada que desplazar porque el texto
 * cabe entero) también cuenta; así nunca queda bloqueado por un redondeo del margen en iOS Safari.
 * Una vez `true` no vuelve a `false`. Sin `IntersectionObserver` (navegadores muy viejos, jsdom) se
 * considera leído de inmediato: no se bloquea a nadie por falta de API.
 */
import { useCallback, useEffect, useState } from 'react';

/** Tolerancia en px para el borde inferior (zoom y subpíxeles de iOS). */
const BOTTOM_TOLERANCE_PX = 2;

function atDocumentBottom(): boolean {
  const doc = document.documentElement;
  if (doc.scrollHeight <= 0) {
    // Sin layout real (jsdom): no se puede decidir por medidas.
    return false;
  }
  return window.innerHeight + window.scrollY >= doc.scrollHeight - BOTTOM_TOLERANCE_PX;
}

export interface ReachedEnd {
  /** Ref de callback para el centinela colocado justo después del texto. */
  sentinelRef: (node: HTMLElement | null) => void;
  reached: boolean;
}

export function useReachedEnd(): ReachedEnd {
  const [node, setNode] = useState<HTMLElement | null>(null);
  const [reached, setReached] = useState(() => typeof IntersectionObserver === 'undefined');
  const sentinelRef = useCallback((next: HTMLElement | null) => setNode(next), []);

  useEffect(() => {
    if (reached || node === null) {
      return;
    }

    const footer = node
      .closest('[data-fm-screen]')
      ?.querySelector<HTMLElement>('[data-screen-footer]');
    const footerHeight = Math.round(footer?.offsetHeight ?? 0);

    const observer = new IntersectionObserver(
      (entries) => {
        if (entries.some((entry) => entry.isIntersecting)) {
          setReached(true);
        }
      },
      { root: null, rootMargin: `0px 0px -${footerHeight}px 0px`, threshold: 0 },
    );
    observer.observe(node);

    const onScroll = (): void => {
      if (atDocumentBottom()) {
        setReached(true);
      }
    };
    onScroll();
    window.addEventListener('scroll', onScroll, { passive: true });
    window.addEventListener('resize', onScroll);

    return () => {
      observer.disconnect();
      window.removeEventListener('scroll', onScroll);
      window.removeEventListener('resize', onScroll);
    };
  }, [node, reached]);

  return { sentinelRef, reached };
}
